import type { AIStore } from '..';
import type { UIMessage } from 'ai';
import type { StateCreator } from 'zustand';

import type useLocalStorage from '@core/hooks/useLocalStorage';
import { actionLog } from '@core/store/util';

import { cacheKey, ChatMessagesStorage, failSilently } from './storage';

export interface ChatModel {
  enabled: boolean;
  id: string;
  name: string;
  provider: string;
}

interface MessageMetadata {
  createdAt: Date;
}

export type MessageWithMetadata = UIMessage & {
  metadata: MessageMetadata;
};

interface Checkpoint {
  action: string;
  content: Record<string, unknown> | string;
  messages: MessageWithMetadata[];
  timestamp: Date;
}

interface AgentContext {
  /**
   * Hidden context is not shown to the user, but included in the prompt
   */
  hiddenContext?: Record<string, string>;
  message?: string;
  prompt?: string;
  runImmediately?: boolean;
  title?: string;
  type?: string;
}

interface AiAgentSliceState {
  chatId: string | null;
  checkpoints: Checkpoint[];
  /**
   * Context for pre-populating chat input when navigating from other components.
   */
  context?: AgentContext;
  model: ChatModel;
  /**
   * Flag to indicate messages should be reset
   */
  resetFlag: boolean;
  storage?: ReturnType<typeof useLocalStorage>;
}

interface AiAgentSliceAction {
  addCheckpoint: (checkpoint: {
    content: Record<string, unknown> | string;
    action: string;
    messages: MessageWithMetadata[];
  }) => number;
  /**
   * Clears context after it has been consumed.
   */
  clearContext: () => void;
  /**
   * Clears the reset flag after useChat has processed it
   */
  clearResetFlag: () => void;
  initialize: (params: { chatId: string; storage: ReturnType<typeof useLocalStorage> }) => void;
  popCheckpoint: () => void;
  reset: () => void;
  /**
   * Resets messages in storage and useChat hook
   */
  resetMessages: (setMessages?: (messages: MessageWithMetadata[]) => void) => void;
  /**
   * Sets AI context for pre-populating chat input.
   */
  setContext: (context: AgentContext) => void;
  setModel: (model: ChatModel) => void;
  updateChatId: (chatId: string, messageCount: number) => void;
}

export interface AiAgentSlice {
  agent: AiAgentSliceAction & AiAgentSliceState;
}

export const defaultModel = {
  provider: 'anthropic',
  id: 'claude-sonnet-4',
  name: 'Claude Sonnet 4',
  enabled: true,
};

const initialState: AiAgentSliceState = {
  chatId: null,
  checkpoints: [],
  context: {},
  model: defaultModel,
  resetFlag: false,
  storage: undefined,
};

export const createAiAgentSlice: StateCreator<
  AIStore,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  AiAgentSlice
> = (set, get) => ({
  agent: {
    ...initialState,

    addCheckpoint: checkpoint => {
      const newCheckpoint: Checkpoint = {
        ...checkpoint,
        timestamp: new Date(),
      };

      set(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => {
          state.agent.checkpoints.push(newCheckpoint);
        },
        false,
        actionLog('ai.agent.addCheckpoint', newCheckpoint),
      );

      return get().agent.checkpoints.length;
    },

    clearContext: () => {
      set(
        state => {
          state.agent.context = {};
        },
        false,
        actionLog('ai.agent.clearContext'),
      );
    },

    initialize: ({ chatId, storage }) => {
      const model = storage.getItem('model') as ChatModel | null;

      // TEMPORARY: Remove Google model from localStorage. Remove if we add Google back.
      if (model?.provider === 'google') storage.removeItem('model');

      set(
        state => {
          Object.assign(state.agent, {
            ...initialState,
            chatId,
            storage,
          });

          if (model) {
            state.agent.model = model;
          }
        },
        false,
        actionLog('ai.agent.initialize', { chatId }),
      );
    },

    popCheckpoint: () => {
      set(
        state => {
          state.agent.checkpoints.pop();
        },
        false,
        actionLog('ai.agent.popCheckpoint'),
      );
    },

    reset: () => {
      set(
        state => {
          Object.assign(state.agent, initialState);
        },
        false,
        actionLog('ai.agent.reset'),
      );
    },

    resetMessages: setMessages => {
      const { chatId, storage } = get().agent;
      if (!chatId || !storage) return;

      // Clear messages from storage
      ChatMessagesStorage.remove(chatId);

      // Also clear old cache key for backwards compatibility
      failSilently(() => storage.removeItem(cacheKey(chatId)));

      if (setMessages) {
        // Clear useChat messages directly if setMessages function is provided
        setMessages([]);
      } else {
        // Set flag to trigger useChat reset from external components
        set(
          state => {
            state.agent.resetFlag = true;
          },
          false,
          actionLog('ai.agent.resetMessages'),
        );
      }
    },

    clearResetFlag: () => {
      set(
        state => {
          state.agent.resetFlag = false;
        },
        false,
        actionLog('ai.agent.clearResetFlag'),
      );
    },

    setContext: context => {
      set(
        state => {
          state.agent.context = context;
        },
        false,
        actionLog('ai.agent.setContext', { context }),
      );
    },

    setModel: model => {
      set(
        state => {
          state.agent.model = model;
        },
        false,
        actionLog('ai.agent.setModel', { model }),
      );
      const { storage } = get().agent;
      if (!storage) return;

      failSilently(() => storage.setItem('model', model));
    },

    updateChatId: (chatId: string, messageCount: number) => {
      set(
        state => {
          state.agent.chatId = chatId;
        },
        false,
        actionLog('ai.agent.updateChatId', { chatId, messageCount }),
      );
    },
  },
});
